import { useState, useEffect } from "react";
import classNames from "classnames";
import { IconPlus, IconTrash } from "@tabler/icons-react"
import { Head, useForm } from "@inertiajs/react";
import { toast } from "sonner";

import DefaultContainer from "@/components/default_container";
import H1 from "@/components/ui/h1";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldError, FieldLabel, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { FormSidebar } from "@/components/form_sidebar";
import { Checkbox } from "@/components/ui/checkbox";
import { WorkingHoursSelect } from "@/components/working_hours_select";
import { WeekDaysSelect } from "@/components/week_days_select";

export default function EditAsset({
  assets: serverAssets,
  current_team,
  asset_type,
  uid_map = {},
}) {
  const [activeAssetUid, setActiveAssetUid] = useState(null);
  const [idsToDelete, setIdsToDelete] = useState([]);
  const {
    data,
    setData,
    transform,
    put,
    processing,
    errors,
    clearErrors
  } = useForm({
    asset_type: {
      working_hours_start: asset_type.working_hours_start,
      working_hours_end: asset_type.working_hours_end,
      working_wdays: asset_type.working_wdays,
    },
    assets: {},
  });

  const activeAsset = data.assets[activeAssetUid];
  const activeAssetErrors = errors[activeAssetUid] || {};

  transform((data) => {
    const toDelete = idsToDelete.map(id => ({ id, _destroy: true }));
    const plainAssets = Object.entries(data.assets).map(([uid, asset]) => ({ uid, ...asset }));
    return {
      asset_type: data.asset_type,
      assets: plainAssets.concat(toDelete),
    };
  });

  const submit = () => {
    const requestPromise = new Promise((resolve, reject) => {
      put(`/${current_team.slug}/asset_types/${asset_type.id}/assets`, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: (page) => { setIdsToDelete([]); resolve(page); },
        onError: (errors) => reject(errors),
      });
    });

    toast.promise(requestPromise, {
      loading: "Saving...",
      success: "Updated successfully",
      error: "An unexpected error occurred",
    });
  };

  const updateAsset = (uid, changes) => {
    setData(prev => ({
      ...prev,
      assets: { ...prev.assets, [uid]: { ...prev.assets[uid], ...changes } },
    }));
    clearErrors(uid);
  };

  const updateAssetType = (changes) => {
    setData(prev => ({ ...prev, asset_type: { ...prev.asset_type, ...changes } }));
    clearErrors("asset_type");
  };

  const addAsset = () => {
    const { uid, asset } = createAsset();
    setData(prev => ({ ...prev, assets: { ...prev.assets, [uid]: asset } }));
    setActiveAssetUid(uid);
  };

  const createAsset = () => {
    const uid = `tmp-${crypto.randomUUID()}`;
    return { uid, asset: { name: "" } };
  };

  const onDelete = (uid) => {
    const asset = data.assets[uid];

    if (asset.id) {
      setIdsToDelete(prevIdsToDelete => [...prevIdsToDelete, asset.id]);
    }

    setData(prev => {
      const { [uid]: _, ...rest } = prev.assets;
      if (uid === activeAssetUid) {
        const nextUid = Object.keys(rest)[0] ?? null;
        setActiveAssetUid(nextUid);
      }
      return { ...prev, assets: rest };
    });
  };

  useEffect(() => {
    if (serverAssets.length > 0) {
      const mapped = {};
      serverAssets.forEach(a => { mapped[`db-${a.id}`] = a; });
      const resolvedUid = uid_map[activeAssetUid] ?? activeAssetUid;
      setActiveAssetUid(mapped[resolvedUid] ? resolvedUid : Object.keys(mapped)[0]);
      setData(prev => ({ ...prev, assets: mapped }));
    } else {
      const { uid, asset } = createAsset();
      setData(prev => ({ ...prev, assets: { [uid]: asset } }));
      setActiveAssetUid(uid);
    }
  }, [serverAssets]);

  return (
    <div className="flex w-full">
      <Head title={`Manage ${asset_type.name_plural}`} />
      <DefaultContainer>
        <div className="flex flex-col gap-8 w-full">
          <H1>Manage {asset_type.name_plural}</H1>

          <div className="w-full">
            {activeAsset && (
              <FieldSet>
                {activeAssetErrors.base && <FieldError>{activeAssetErrors.base}</FieldError>}
                <FieldGroup>
                  <Field data-invalid={!!activeAssetErrors.name}>
                    <FieldLabel htmlFor={`name-${activeAssetUid}`}>Name</FieldLabel>
                    <Input
                      placeholder="Big Meeting Room"
                      value={activeAsset.name}
                      onChange={(e) => updateAsset(activeAssetUid, { name: e.target.value }) }
                      disabled={processing}
                      aria-invalid={!!activeAssetErrors.name}
                    />
                    {activeAssetErrors.name && (<FieldError>{activeAssetErrors.name}</FieldError>)}
                  </Field>
                  <div className="flex flex-col gap-3">
                    <FieldLabel>Working hours</FieldLabel>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`hours-override-${activeAssetUid}`}
                        checked={activeAsset.own_working_hours_start != null || activeAsset.own_working_hours_end != null}
                        onCheckedChange={(checked) =>
                          updateAsset(activeAssetUid, {
                            own_working_hours_start: checked ? data.asset_type.working_hours_start : null,
                            own_working_hours_end: checked ? data.asset_type.working_hours_end : null,
                          })
                        }
                      />
                      <label
                        htmlFor={`hours-override-${activeAssetUid}`}
                        className="text-sm text-muted-foreground cursor-pointer"
                      >
                        Override default working hours
                      </label>
                    </div>
                    <div className="flex gap-2 items-center">
                      <span className="text-sm text-muted-foreground">from</span>
                      <WorkingHoursSelect
                        value={activeAsset.own_working_hours_start ?? data.asset_type.working_hours_start}
                        onChange={(v) => updateAsset(activeAssetUid, { own_working_hours_start: v })}
                        invalid={!!activeAssetErrors.working_hours_start}
                        disabled={activeAsset.own_working_hours_start == null && activeAsset.own_working_hours_end == null}
                      />
                      <span className="text-sm text-muted-foreground">to</span>
                      <WorkingHoursSelect
                        value={activeAsset.own_working_hours_end ?? data.asset_type.working_hours_end}
                        onChange={(v) => updateAsset(activeAssetUid, { own_working_hours_end: v })}
                        invalid={!!activeAssetErrors.working_hours_end}
                        disabled={activeAsset.own_working_hours_start == null && activeAsset.own_working_hours_end == null}
                      />
                    </div>
                    {activeAssetErrors.working_hours_start && <FieldError>Working hours start {activeAssetErrors.working_hours_start}</FieldError>}
                    {activeAssetErrors.working_hours_end && <FieldError>Working hours end {activeAssetErrors.working_hours_end}</FieldError>}
                  </div>
                  <div className="flex flex-col gap-3">
                    <FieldLabel>Working days</FieldLabel>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`wdays-override-${activeAssetUid}`}
                        checked={activeAsset.own_working_wdays != null}
                        onCheckedChange={(checked) =>
                          updateAsset(activeAssetUid, {
                            own_working_wdays: checked ? data.asset_type.working_wdays : null,
                          })
                        }
                      />
                      <label
                        htmlFor={`wdays-override-${activeAssetUid}`}
                        className="text-sm text-muted-foreground cursor-pointer"
                      >
                        Override default working days
                      </label>
                    </div>
                    <WeekDaysSelect
                      value={activeAsset.own_working_wdays ?? data.asset_type.working_wdays}
                      onChange={(v) => updateAsset(activeAssetUid, { own_working_wdays: v })}
                      errors={activeAssetErrors.working_wdays_bit}
                      disabled={activeAsset.own_working_wdays == null}
                    />
                  </div>
                </FieldGroup>
              </FieldSet>
            )}
          </div>
        </div>
      </DefaultContainer>

      <FormSidebar size="md">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <FieldLabel>Default working days</FieldLabel>
            <WeekDaysSelect
              value={data.asset_type.working_wdays}
              onChange={(v) => updateAssetType({ working_wdays: v })}
              errors={errors.asset_type?.working_wdays_bit}
            />
          </div>
          <div className="flex flex-col gap-3">
            <FieldLabel>Default working hours</FieldLabel>
            {errors.asset_type?.base && <FieldError>{errors.asset_type.base}</FieldError>}
            <div className="flex gap-2 items-center">
              <span className="text-sm text-muted-foreground">from</span>
              <WorkingHoursSelect
                value={data.asset_type.working_hours_start}
                onChange={(v) => updateAssetType({ working_hours_start: v })}
                invalid={!!errors.asset_type?.working_hours_start}
              />
              <span className="text-sm text-muted-foreground">to</span>
              <WorkingHoursSelect
                value={data.asset_type.working_hours_end}
                onChange={(v) => updateAssetType({ working_hours_end: v })}
                invalid={!!errors.asset_type?.working_hours_end}
              />
            </div>
            {errors.asset_type?.working_hours_end && (
              <FieldError>Working hours end {errors.asset_type.working_hours_end}</FieldError>
            )}
          </div>

          <hr />

          {Object.entries(data.assets).map(([uid, asset]) => (
            <AssetRow
              key={uid}
              asset={asset}
              active={activeAssetUid === uid}
              valid={!errors[uid]}
              onClick={() => setActiveAssetUid(uid)}
              onDelete={() => onDelete(uid)}
            />
          ))}
          <Button variant="outline" onClick={addAsset} disabled={processing}>Add <IconPlus /></Button>
          <Button onClick={submit} disabled={processing}>Save</Button>
        </div>
      </FormSidebar>
    </div>
  );
}

function AssetRow({ asset, onClick, onDelete, valid = false, active = false }) {
  return (
    <div className="flex gap-1 items-center">
      <Button
        variant="outline"
        onClick={onClick}
        aria-invalid={!valid}
        className={classNames(
          "flex-grow",
          {
            "bg-gray-100": active,
            "text-gray-400": !asset.name,
            "hover:text-gray-400": !asset.name,
          }
        )}
      >
        {asset.name ? asset.name : "Untitled"}
      </Button>

      <Button variant="outline" size="icon" onClick={onDelete}><IconTrash /></Button>
    </div>
  );
}
